/**
 * Shared flight dedup logic.
 * Used by XLS import (browser), CSV import (browser), and CLI migrate-excel.js.
 *
 * The composite key is: flight_date|aircraft_id|pilot_id|block_time_mins
 * Two flights are considered duplicates if all four fields match.
 *
 * Requirements: 20.1, 20.2, 20.4
 */

/**
 * Build a dedup key string from a flight payload.
 * Key = `${flight_date}|${aircraft_id}|${pilot_id}|${block_time_mins}`
 *
 * @param {Object} flight - { flight_date, aircraft_id, pilot_id, block_time_mins }
 * @returns {string}
 */
export function buildDedupKey(flight) {
  return `${flight.flight_date}|${flight.aircraft_id}|${flight.pilot_id}|${flight.block_time_mins}`;
}

/**
 * Given an array of mapped flight payloads (with resolved IDs), query the
 * database for existing flights matching any of the composite keys and return
 * a Set of matched keys.
 *
 * The query fetches all flights whose (flight_date, aircraft_id, pilot_id)
 * tuples overlap with the candidates, then filters client-side on
 * block_time_mins to build the final Set.
 *
 * @param {Object[]} flights - Array of flight payloads with resolved IDs.
 *   Each must have { flight_date, aircraft_id, pilot_id, block_time_mins }
 * @param {Object} [supabaseClient] - Optional Supabase client instance.
 *   If omitted, uses getSupabase() from the browser service layer.
 *   CLI callers should pass their service-role client here.
 * @returns {Promise<Set<string>>} Set of dedup key strings that already exist
 */
export async function findExistingFlights(flights, supabaseClient) {
  if (!flights || flights.length === 0) return new Set();

  // Resolve the Supabase client: use the provided one (CLI) or fall back to
  // the browser singleton. The dynamic import avoids a hard dependency on the
  // browser service layer when running under Node.
  let client = supabaseClient;
  if (!client) {
    const { getSupabase } = await import('../services/supabase.js');
    client = getSupabase();
  }

  // Collect unique filter values from the candidate flights
  const dates = [...new Set(flights.map((f) => f.flight_date))];
  const aircraftIds = [...new Set(flights.map((f) => f.aircraft_id))];
  const pilotIds = [...new Set(flights.map((f) => f.pilot_id))];

  // Batch query: fetch existing flights matching any combination of the
  // candidate dates, aircraft, and pilots. This is intentionally broad —
  // the precise four-field match is done client-side below.
  const { data, error } = await client
    .from('flights')
    .select('flight_date, aircraft_id, pilot_id, block_time_mins')
    .in('flight_date', dates)
    .in('aircraft_id', aircraftIds)
    .in('pilot_id', pilotIds);

  if (error) {
    throw new Error(`Dedup query failed: ${error.message}`);
  }

  // Build a Set of composite keys from the DB results
  const existingKeys = new Set();
  for (const row of data || []) {
    existingKeys.add(buildDedupKey(row));
  }

  return existingKeys;
}
